import React, { FC, Suspense, useMemo, useState } from 'react';
import { ErrorBoundary } from '@funtech-int/rival-react';
import { Episode } from '@funtech-int/kp-graphql-sdk/yango-movies-www';
import { useAnalytics } from '@funtech-int/yango-shared';

import { createSeriesStructureAnalytics } from '../../lib/seriesStructureAnalytics';

import styles from './PlayerSeriesStructure.module.css';
import { SeasonsList } from './_SeasonsList';
import { EpisodesList } from './_EpisodesList';
import { SeasonHeader } from './_SeasonHeader';

export interface PlayerSeriesStructureProps {
    filmId: string;
    seasonId: string;
    currentEpisodeTiming: number;
    playingContentId: string;
    onEpisodeNavigate?: (episode: Episode) => void;
}

const PlayerSeriesStructureComponent: FC<PlayerSeriesStructureProps> = ({
    filmId,
    seasonId,
    currentEpisodeTiming,
    playingContentId,
    onEpisodeNavigate,
}) => {
    const [isAllSeasonsVisible, setIsAllSeasonsVisible] = useState(false);
    const [selectedSeasonId, setSelectedSeasonId] = useState<string>(seasonId);
    const analytics = useAnalytics(createSeriesStructureAnalytics);

    const controller = useMemo(
        () => ({
            selectSeason: (contentId: string) => {
                setIsAllSeasonsVisible(false);
                setSelectedSeasonId(contentId);
            },
            openAllSeasons: () => {
                setIsAllSeasonsVisible(true);
            },
            onEpisodeClick: (episode: Episode) => {
                if (!episode.isPublished || !episode.viewOption?.isWatchable) {
                    return;
                }

                analytics.episodeClicked(
                    filmId,
                    episode.season?.number ?? 0,
                    episode.title.toString() ?? ''
                );

                onEpisodeNavigate?.(episode);
            },
        }),
        [filmId, analytics, onEpisodeNavigate]
    );

    if (isAllSeasonsVisible) {
        return <SeasonsList filmId={filmId} onSelectSeason={controller.selectSeason} />;
    }

    return (
        <>
            <SeasonHeader
                filmId={filmId}
                seasonId={selectedSeasonId}
                onOpenAllSeasons={controller.openAllSeasons}
            />
            <EpisodesList
                seasonId={selectedSeasonId}
                filmId={filmId}
                currentEpisodeTiming={currentEpisodeTiming}
                onEpisodeClick={controller.onEpisodeClick}
                playingContentId={playingContentId}
            />
        </>
    );
};

export const PlayerSeriesStructure = ({
    filmId,
    seasonId,
    currentEpisodeTiming,
    playingContentId,
    onEpisodeNavigate,
}: PlayerSeriesStructureProps) => (
    <div className={styles.root}>
        <ErrorBoundary fallback={() => null}>
            <Suspense fallback={null}>
                <div className={styles.inner}>
                    <PlayerSeriesStructureComponent
                        filmId={filmId}
                        seasonId={seasonId}
                        currentEpisodeTiming={currentEpisodeTiming}
                        playingContentId={playingContentId}
                        onEpisodeNavigate={onEpisodeNavigate}
                    />
                </div>
            </Suspense>
        </ErrorBoundary>
    </div>
);
